// Google Analytics Service for Cospin Website
// Handles GA4 tracking, events, and user interactions

class AnalyticsService {
    constructor() {
        this.measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || '';
        this.isInitialized = false;
        this.hasConsent = false;
    }

    // Initialize Google Analytics
    initialize() {
        if (this.isInitialized || !this.measurementId) {
            console.log('Analytics: No measurement ID provided, skipping initialization');
            return;
        }

        // Load Google Analytics script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
        document.head.appendChild(script);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };

        // Configure GA4
        window.gtag('js', new Date());
        window.gtag('config', this.measurementId, {
            page_title: document.title,
            page_location: window.location.href,
            send_page_view: false // We'll handle this manually
        });

        this.isInitialized = true;
        console.log('Google Analytics initialized');
    }

    // Set user consent
    setConsent(hasConsent) {
        this.hasConsent = hasConsent;

        if (hasConsent && !this.isInitialized) {
            this.initialize();
        }

        // Update consent state in GA4
        if (this.isInitialized) {
            window.gtag('consent', 'update', {
                analytics_storage: hasConsent ? 'granted' : 'denied',
                ad_storage: hasConsent ? 'granted' : 'denied'
            });
        }
    }

    // Track page view
    trackPageView(pageTitle, pagePath) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'page_view', {
            page_title: pageTitle,
            page_location: pagePath,
            page_referrer: document.referrer
        });
    }

    // Track form submission
    trackFormSubmission(formType, formData) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'form_submit', {
            form_type: formType,
            form_name: formData.formName || 'Unknown',
            user_email: formData.email ? formData.email.substring(0, 3) + '***' : 'anonymous',
            user_company: formData.company || 'Not specified',
            service_interest: formData.service || 'Not specified'
        });
    }

    // Track case study download
    trackCaseStudyDownload(caseStudyName, userData) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'download', {
            content_type: 'case_study',
            content_name: caseStudyName,
            user_email: userData.email ? userData.email.substring(0, 3) + '***' : 'anonymous',
            user_company: userData.company || 'Not specified'
        });
    }

    // Track button clicks
    trackButtonClick(buttonName, buttonLocation) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'button_click', {
            button_name: buttonName,
            button_location: buttonLocation,
            page_title: document.title
        });
    }

    // Track external link clicks
    trackExternalLink(url, linkText) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'click_external_link', {
            link_url: url,
            link_text: linkText,
            page_title: document.title
        });
    }

    // Track scroll depth
    trackScrollDepth(depth) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'scroll', {
            scroll_depth: depth,
            page_title: document.title
        });
    }

    // Track time on page
    trackTimeOnPage(timeSpent) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', 'timing_complete', {
            name: 'page_view',
            value: timeSpent,
            page_title: document.title
        });
    }

    // Track custom events
    trackCustomEvent(eventName, parameters = {}) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('event', eventName, {
            ...parameters,
            page_title: document.title,
            timestamp: new Date().toISOString()
        });
    }

    // Get user properties for enhanced analytics
    setUserProperties(properties) {
        if (!this.isInitialized || !this.hasConsent) {
            return;
        }

        window.gtag('config', this.measurementId, {
            custom_map: properties
        });
    }

    // Debug mode for development
    enableDebugMode() {
        if (this.isInitialized) {
            window.gtag('config', this.measurementId, {
                debug_mode: true
            });
        }
    }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
